import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';

import { InvestorDetailsLegalEntityAssetClassTargetsQuery } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { currencyFormatter, formatCash } from '@/shared/utils/currency';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import {
  overCommitmentRatioModeSummaryTableColumns,
  pacingModelModeSimplifiedSummaryTableColumns,
  pacingModelModeSummaryTableColumns
} from '../../../constants';
import CapacityCell from '../cell/capacityCell';
import SummaryMobileTableSumFooter from './mobileSumFooter';

const economicExposureToolTipContent = (
  row:
    | InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]
    | InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]['subAssetClasses'][number]
) => {
  return (
    <TableBodyWrapper>
      <TableRowCell width={100}>
        <TextCell>Current NAV: {currencyFormatter.format(row.currentNAV)}</TextCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TextCell>Unfunded Commitment: {currencyFormatter.format(row.unfundedCommitments)}</TextCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TextCell>Economic Exposure: {currencyFormatter.format(row.economicExposure)}</TextCell>
      </TableRowCell>
    </TableBodyWrapper>
  );
};

interface SummaryMobileTableProps {
  investorSummaryRows: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'];
  ignoreAllocationTargets: boolean;
  commitmentPlansTotal: number | null;
  targetOCRatio: number | null;
  targetSubAssetClasses: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
}

const SummaryMobileTable = ({
  investorSummaryRows,
  ignoreAllocationTargets,
  commitmentPlansTotal,
  targetOCRatio,
  targetSubAssetClasses,
  entityType
}: SummaryMobileTableProps) => {
  const TableRow = ({ el }: { el: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number] }) => {
    // if (ignoreAllocationTargets && targetOCRatio !== null) {
    //   return (
    //     <MobileTableRowWrap key={el.id}>
    //       <MobileRow>
    //         <MobileRowTitle>{overCommitmentRatioModeSimplifiedSummaryTableColumns[0].title}</MobileRowTitle>
    //         <MobileRowValue>{el.name}</MobileRowValue>
    //       </MobileRow>

    //       <MobileRow>
    //         <MobileRowTitle>{overCommitmentRatioModeSimplifiedSummaryTableColumns[1].title}</MobileRowTitle>
    //         <MobileRowValue>
    //           <FormattingTooltip zIndex={1000}>{el.economicExposure}</FormattingTooltip>
    //         </MobileRowValue>
    //       </MobileRow>
    //     </MobileTableRowWrap>
    //   );
    // }

    if (ignoreAllocationTargets || entityType === InvestorDetailsEntityTypeEnum.GroupedEntity) {
      return (
        <MobileTableRowWrap key={el.id}>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSimplifiedSummaryTableColumns[0].title}</MobileRowTitle>
            <MobileRowValue>{el.name}</MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{pacingModelModeSimplifiedSummaryTableColumns[1].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip zIndex={1000}>{el.currentNAV}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{pacingModelModeSimplifiedSummaryTableColumns[2].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip zIndex={1000}>{el.unfundedCommitments}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>
        </MobileTableRowWrap>
      );
    }

    if (targetOCRatio === null) {
      return (
        <MobileTableRowWrap key={el.id}>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSummaryTableColumns[0].title}</MobileRowTitle>
            <MobileRowValue>{el.name}</MobileRowValue>
          </MobileRow>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSummaryTableColumns[1].title}</MobileRowTitle>
            <MobileRowValue>{el.percentage === null ? '--' : `${el.percentage.toFixed(2)}%`}</MobileRowValue>
          </MobileRow>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSummaryTableColumns[2].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip zIndex={1000}>{el.targetAllocation}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSummaryTableColumns[3].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip zIndex={1000}>{el.currentNAV}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSummaryTableColumns[4].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip zIndex={1000}>{el.unfundedCommitments}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>
          <MobileRow>
            <MobileRowTitle>{pacingModelModeSummaryTableColumns[5].title}</MobileRowTitle>
            <MobileRowValue>
              <CapacityCell
                row={el}
                commitmentPlansTotal={commitmentPlansTotal}
                targetOCRatio={targetOCRatio}
                isMobile
                targetSubAssetClasses={targetSubAssetClasses}
              />
            </MobileRowValue>
          </MobileRow>
        </MobileTableRowWrap>
      );
    }

    return (
      <MobileTableRowWrap key={el.id}>
        <MobileRow>
          <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[0].title}</MobileRowTitle>
          <MobileRowValue>{el.name}</MobileRowValue>
        </MobileRow>
        <MobileRow>
          <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[1].title}</MobileRowTitle>
          <MobileRowValue>{el.percentage === null ? '--' : `${el.percentage.toFixed(2)}%`}</MobileRowValue>
        </MobileRow>
        <MobileRow>
          <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[2].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{el.targetAllocation}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>
        <MobileRow>
          <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[3].title}</MobileRowTitle>
          <MobileRowValue>
            <CellWithTooltip>
              {formatCash(el.economicExposure)}
              <Tooltip title={economicExposureToolTipContent(el)}>
                <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
              </Tooltip>
            </CellWithTooltip>
          </MobileRowValue>
        </MobileRow>
        <MobileRow>
          <MobileRowTitle>{overCommitmentRatioModeSummaryTableColumns[4].title}</MobileRowTitle>
          <MobileRowValue>
            <CapacityCell
              row={el}
              commitmentPlansTotal={commitmentPlansTotal}
              targetOCRatio={targetOCRatio}
              isMobile
              targetSubAssetClasses={targetSubAssetClasses}
            />
          </MobileRowValue>
        </MobileRow>
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {investorSummaryRows?.length ? (
        <>
          {investorSummaryRows.map((el, index) => {
            return <TableRow el={el} key={index} />;
          })}
          <SummaryMobileTableSumFooter
            investorSummaryRows={investorSummaryRows}
            ignoreAllocationTargets={ignoreAllocationTargets}
            commitmentPlansTotal={commitmentPlansTotal}
            targetOCRatio={targetOCRatio}
            targetSubAssetClasses={targetSubAssetClasses}
            entityType={entityType}
          />
        </>
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default SummaryMobileTable;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
